/**
 * ColorPickerComponent
 *
 * Input component for color selection in forms
 * Renders HTML5 color picker with optional alpha slider
 *
 * Features:
 * - Native color picker
 * - Hex code display
 * - Opacity control
 * - Accessibility: ARIA labels, keyboard navigation
 * - Validation support
 */

import React, { useCallback, useMemo, memo } from 'react';
import type { FormControlProps } from '../../types';

export interface ColorValue {
  hex: string;
  alpha: number;
  rgb?: string;
  hsla?: string;
}

interface ColorPickerComponentProps extends FormControlProps {
  defaultColor?: string;
  showAlpha?: boolean;
  defaultAlpha?: number;
  presets?: string[];
  format?: 'hex' | 'rgb' | 'hsla';
  onChange?: (value: ColorValue) => void;
}

/**
 * Helper: Convert hex to RGB
 */
function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : null;
}

/**
 * Helper: Convert RGB to HSLA
 */
function rgbToHsla(r: number, g: number, b: number, a: number = 100): string {
  r /= 255;
  g /= 255;
  b /= 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
    }
  }

  const hVal = Math.round(h * 360);
  const sVal = Math.round(s * 100);
  const lVal = Math.round(l * 100);
  const aVal = a / 100;

  return `hsla(${hVal}, ${sVal}%, ${lVal}%, ${aVal})`;
}

/**
 * Helper: Create color value object
 */
function createColorValue(hex: string, alpha: number, format: string = 'hex'): ColorValue {
  const rgb = hexToRgb(hex);
  const colorValue: ColorValue = {
    hex,
    alpha,
  };

  if (rgb) {
    colorValue.rgb = `rgb(${rgb.r}, ${rgb.g}, ${rgb.b})`;
    colorValue.hsla = rgbToHsla(rgb.r, rgb.g, rgb.b, alpha);
  }

  return colorValue;
}

/**
 * ColorPickerComponent
 * Renders a color picker with optional alpha channel
 * Performance: Memoized to prevent unnecessary re-renders
 */
export const ColorPickerComponent: React.FC<ColorPickerComponentProps> = memo(({
  value = '#000000',
  defaultColor = '#000000',
  showAlpha = true,
  defaultAlpha = 100,
  presets = [],
  format = 'hex',
  onChange,
  disabled = false,
  required = false,
  label,
  error,
  helpText,
}) => {
  const currentColor = (value as string) || defaultColor;
  const currentAlpha = useMemo(() => {
    if (typeof value === 'object' && value !== null && 'alpha' in value) {
      return (value as ColorValue).alpha;
    }
    return defaultAlpha;
  }, [value, defaultAlpha]);

  const handleColorChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const hex = e.currentTarget.value;
      const colorValue = createColorValue(hex, currentAlpha, format);
      onChange?.(colorValue);
    },
    [currentAlpha, format, onChange],
  );

  const handleAlphaChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const alpha = Number(e.currentTarget.value);
      const rgb = hexToRgb(currentColor);
      const colorValue: ColorValue = {
        hex: currentColor,
        alpha,
      };

      if (rgb) {
        colorValue.rgb = `rgb(${rgb.r}, ${rgb.g}, ${rgb.b})`;
        colorValue.hsla = rgbToHsla(rgb.r, rgb.g, rgb.b, alpha);
      }

      onChange?.(colorValue);
    },
    [currentColor, onChange],
  );

  const handlePresetClick = useCallback(
    (preset: string) => {
      const colorValue = createColorValue(preset, currentAlpha, format);
      onChange?.(colorValue);
    },
    [currentAlpha, format, onChange],
  );

  const inputId = useMemo(
    () => `color-picker-${label?.replace(/\s+/g, '-').toLowerCase()}`,
    [label],
  );

  return (
    <div className="af-form-field__group color-picker-group">
      {label && (
        <label htmlFor={inputId} className="af-form-field__label">
          {label}
          {required && <span className="af-form-field__required">*</span>}
        </label>
      )}

      <div className="color-picker__container">
        <div className="color-picker__row">
          <input
            id={inputId}
            type="color"
            value={currentColor}
            onChange={handleColorChange}
            disabled={disabled}
            className="color-picker__input"
            aria-label={label || 'Color picker'}
            aria-invalid={!!error}
            aria-describedby={error ? `${inputId}-error` : helpText ? `${inputId}-help` : undefined}
          />

          <div
            className="color-picker__preview"
            style={{
              backgroundColor: currentColor,
              opacity: showAlpha ? currentAlpha / 100 : 1,
            }}
            role="img"
            aria-label={`Color: ${currentColor}, Alpha: ${currentAlpha}%`}
          />

          <input
            type="text"
            value={currentColor}
            onChange={(e) => {
              let val = e.currentTarget.value.trim();
              if (!val.startsWith('#')) val = '#' + val;
              if (/^#[0-9A-Fa-f]{6}$/.test(val)) {
                const colorValue = createColorValue(val, currentAlpha, format);
                onChange?.(colorValue);
              }
            }}
            placeholder="#000000"
            disabled={disabled}
            className="color-picker__hex-input"
            maxLength={7}
            aria-label="Hex color code"
          />
        </div>

        {showAlpha && (
          <div className="color-picker__alpha-group">
            <label htmlFor={`${inputId}-alpha`} className="color-picker__alpha-label">
              Opacity: {currentAlpha}%
            </label>
            <input
              id={`${inputId}-alpha`}
              type="range"
              min="0"
              max="100"
              value={currentAlpha}
              onChange={handleAlphaChange}
              disabled={disabled}
              className="color-picker__alpha-slider"
              aria-label="Opacity"
            />
          </div>
        )}

        {presets.length > 0 && (
          <div className="color-picker__presets">
            {presets.map((preset) => (
              <button
                key={preset}
                type="button"
                onClick={() => handlePresetClick(preset)}
                className="color-picker__preset-button"
                style={{ backgroundColor: preset }}
                disabled={disabled}
                title={preset}
                aria-label={`Select preset color ${preset}`}
              />
            ))}
          </div>
        )}
      </div>

      {error && (
        <div id={`${inputId}-error`} className="af-form-field__error">
          {error}
        </div>
      )}

      {helpText && (
        <div id={`${inputId}-help`} className="af-form-field__help">
          {helpText}
        </div>
      )}
    </div>
  );
});

ColorPickerComponent.displayName = 'ColorPickerComponent';
